import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React from 'react';
import type {
  ChatResolveEntityOption,
  EditableResolveDraftAction,
} from '../../models/admin/AdminChatResolveDraft';
import { orderAttachToOptions } from '../../utils/chatResolveAttachTo';
import {
  formatSuggestedInvestmentLabel,
  getActionAttachToSelectValue,
  getActionDueDateHelperText,
} from './chatResolveDraftsDialogUtils';

export interface ChatResolveDraftActionEditorProps {
  action: EditableResolveDraftAction;
  attachToOptions: ChatResolveEntityOption[];
  onSelectedChange: (draftId: string, selected: boolean) => void;
  onTitleChange: (draftId: string, title: string) => void;
  onDescriptionChange: (draftId: string, description: string) => void;
  onEntityChange: (draftId: string, option: ChatResolveEntityOption) => void;
  onDueAtChange: (draftId: string, dueAt: string) => void;
}

const ChatResolveDraftActionEditor: React.FC<ChatResolveDraftActionEditorProps> = ({
  action,
  attachToOptions,
  onSelectedChange,
  onTitleChange,
  onDescriptionChange,
  onEntityChange,
  onDueAtChange,
}) => {
  const suggestedInvestment = formatSuggestedInvestmentLabel(action.modelLabel);
  const attachToValue = getActionAttachToSelectValue(action, attachToOptions);
  const attachToContext =
    action.modelType && action.modelExternalId
      ? { modelType: action.modelType, modelExternalId: action.modelExternalId }
      : null;

  return (
    <Box
      sx={{
        border: '1px solid #e8e8e8',
        borderRadius: 1,
        p: 1.5,
        mb: 1.5,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={action.selected}
            onChange={(event) => onSelectedChange(action.draftId, event.target.checked)}
          />
        }
        label="Include"
      />
      <TextField
        fullWidth
        label="Title"
        value={action.title}
        onChange={(event) => onTitleChange(action.draftId, event.target.value)}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Description (optional)"
        value={action.description ?? ''}
        onChange={(event) => onDescriptionChange(action.draftId, event.target.value)}
        sx={{ mb: 1.5 }}
      />
      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>Attach to</InputLabel>
        <Select
          label="Attach to"
          value={attachToValue}
          onChange={(event) => {
            const value = String(event.target.value);
            const [modelType, modelExternalId] = value.split(':');
            const option = attachToOptions.find(
              (item) => item.modelType === modelType && item.modelExternalId === modelExternalId,
            );
            if (option) onEntityChange(action.draftId, option);
          }}
        >
          {orderAttachToOptions(attachToOptions, attachToContext).map((option) => (
            <MenuItem
              key={`${option.modelType}:${option.modelExternalId}`}
              value={`${option.modelType}:${option.modelExternalId}`}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {action.modelType === 'INVESTOR' && suggestedInvestment && (
          <FormHelperText>{`Suggested investment: ${suggestedInvestment}`}</FormHelperText>
        )}
      </FormControl>
      <TextField
        type={action.dueAtInputType}
        label={action.dueAtInputType === 'datetime-local' ? 'Due date & time' : 'Due date'}
        value={action.dueAt}
        onChange={(event) => onDueAtChange(action.draftId, event.target.value)}
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
        required={action.selected}
        helperText={getActionDueDateHelperText(action.dueAtConfidence, action.dueAtInputType)}
      />
    </Box>
  );
};

export default ChatResolveDraftActionEditor;
